/* eslint-disable react-hooks/exhaustive-deps */
import { FloatingPanels } from "orcs-design-system";
import React, { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import styled from "styled-components";
import { isEmpty } from "lodash";
import {
  Z_INDEX,
  FULL_SCREEN_SPACING,
  PANEL_POSITIONING,
} from "src/components/NodeVisualizer/constants";
import { useOrgChartContext } from "src/components/OrgChart/context/OrgChartContext";
import { EventBus } from "src/services/eventEmitter";
import {
  addLocalStorageItem,
  getLocalStorageItem,
} from "src/services/localStorage";
import { ORG_CHART_DEFAULT_PANEL_ID } from "src/components/OrgChart/utils/storeInLocalStorage";
import { SOURCE_TYPES } from "src/components/AIContent/consts";
import { useWorkspaceFeatureFlags } from "src/contexts/global/WorkspaceContext";
import { AISummaryWithAgentCore } from "../../../AIContent/AISummaryWithAgentCore";
import ViewOptions from "./panels/ViewOptions";
import Properties from "./panels/Properties";
import Legend from "./panels/Legend";
import { PANEL_ID, ORG_CHART_PANEL_EVENTS } from "./const";
import { Details } from "./panels/Details/Details";

// Wrapper to position panels relative to viewport in full-screen mode
const PanelWrapper = styled.div`
  ${({ isFullScreen }) =>
    isFullScreen &&
    `
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    pointer-events: none;
    z-index: ${Z_INDEX.PANELS}; /* Above canvas (z-index: ${Z_INDEX.CANVAS}) */
    
    > * {
      pointer-events: auto;
    }
  `}
`;

const LegendPanel = {
  id: PANEL_ID.legend,
  iconName: "question-circle",
  title: "Legend",
  content: <Legend />,
};

const ViewOptionsPanel = {
  id: PANEL_ID.viewOptions,
  iconName: "eye",
  title: "View Options",
  content: <ViewOptions />,
};

const PropertiesPanel = {
  id: PANEL_ID.properties,
  iconName: "tag",
  title: "Properties",
  content: <Properties />,
};

const DetailsPanel = {
  id: PANEL_ID.details,
  iconName: "info-circle",
  title: "Details",
  content: <Details />,
};

const AISummaryPanel = () => {
  const { state } = useOrgChartContext();
  const [id, setId] = useState();
  const [isPanelVisible, setIsPanelVisible] = useState(false);

  useEffect(() => {
    const newId =
      state?.expandedPanel?.data?.selectedEntity || state.currentViewTeam?.id;

    if (newId) {
      setId(newId);
    }
  }, [state?.expandedPanel?.data?.selectedEntity, state.currentViewTeam?.id]);

  // Listen for panel selection changes
  useEffect(() => {
    const handlePanelChange = (panelId) => {
      setIsPanelVisible(panelId === PANEL_ID.ai);
    };

    EventBus.on(
      ORG_CHART_PANEL_EVENTS.SELECTED_PANEL_CHANGED,
      handlePanelChange
    );

    return () => {
      EventBus.off(
        ORG_CHART_PANEL_EVENTS.SELECTED_PANEL_CHANGED,
        handlePanelChange
      );
    };
  }, []);

  if (id) {
    return (
      <AISummaryWithAgentCore
        sourceId={SOURCE_TYPES.team_summary}
        subjectId={id}
        title=""
        isPanelVisible={isPanelVisible}
      />
    );
  }
  return undefined;
};

const OrgChartPanel = ({
  hideDetailsPanelInitially = false,
  isFullScreen = false,
  headerHeight = 0,
}) => {
  const { state } = useOrgChartContext();
  const flags = useWorkspaceFeatureFlags();
  const [forcePanelId, setForcePanelId] = useState(null);

  // Force show details panel when entering full-screen mode
  useEffect(() => {
    if (isFullScreen && !hideDetailsPanelInitially) {
      // Set a key to force FloatingPanels to re-initialize with the new defaultPanelId
      setForcePanelId(PANEL_ID.details);
      // Also emit event to set the panel
      EventBus.emit(
        ORG_CHART_PANEL_EVENTS.SET_SELECTED_PANEL,
        PANEL_ID.details
      );
    } else if (!isFullScreen) {
      // Clear forced panel when exiting full-screen
      setForcePanelId(null);
    }
  }, [isFullScreen, hideDetailsPanelInitially]);

  // listen to panel change events so we can retain panel state across page loads
  useEffect(() => {
    const StoreSelectedPanel = (paneId) => {
      // store selected panel in local storage
      addLocalStorageItem(ORG_CHART_DEFAULT_PANEL_ID, paneId);
    };

    // register event listeners
    EventBus.on(
      ORG_CHART_PANEL_EVENTS.SELECTED_PANEL_CHANGED,
      StoreSelectedPanel
    );

    // on unmount, deregister event listeners
    return () => {
      EventBus.off(
        ORG_CHART_PANEL_EVENTS.SELECTED_PANEL_CHANGED,
        StoreSelectedPanel
      );
    };
  }, []);

  // calc panels to display based on state
  const panelsToDisplay = useMemo(() => {
    const panels = [];

    // ai panel
    if (flags?.enableAISummaryOnTeamDetailsPage) {
      const AIPanel = {
        id: PANEL_ID.ai,
        iconName: "sparkles",
        title: "AI Summary",
        content: <AISummaryPanel />,
      };
      panels.push(AIPanel);
    }

    // legend panel (always show)
    panels.push(LegendPanel);

    // view options panel
    panels.push(ViewOptionsPanel);

    // only show properties panel if there are loaded teams or members
    if (!isEmpty(state?.teams) || !isEmpty(state?.members)) {
      panels.push(PropertiesPanel);
    }

    // legend panel (always show)
    panels.push(DetailsPanel);

    return panels;
  }, [state, flags?.enableAISummaryOnTeamDetailsPage]);

  const defaultPanelId = useMemo(() => {
    // If forcing a panel ID (e.g., when entering full-screen), use that
    if (forcePanelId !== null) {
      return forcePanelId;
    }

    // If hiding details panel initially, don't show any panel
    if (hideDetailsPanelInitially) {
      return null;
    }

    // In full-screen mode, always show details panel by default
    if (isFullScreen) {
      return PANEL_ID.details;
    }

    // get default selected panel from local storage
    const storedPanelId = getLocalStorageItem(ORG_CHART_DEFAULT_PANEL_ID);

    // Only use stored panel ID if it's a valid panel ID (not null/undefined)
    // If user closed the panel (null), we still show details panel by default
    if (storedPanelId !== undefined && storedPanelId !== null) {
      return storedPanelId;
    }

    // fallback to details panel (always show by default in normal mode)
    return PANEL_ID.details;
  }, [hideDetailsPanelInitially, isFullScreen, forcePanelId]);

  // Calculate panel position and container height based on full-screen state
  const gapSize = FULL_SCREEN_SPACING.GAP_SIZE;
  const topPadding = gapSize * 2; // Top and bottom padding combined
  const canvasPadding = FULL_SCREEN_SPACING.PANEL_PADDING * 2; // Top + bottom internal padding
  const containerHeight = isFullScreen
    ? `calc(100vh - ${headerHeight}px - ${topPadding}px - ${canvasPadding}px)`
    : `100%`;

  const position = isFullScreen
    ? {
        right: gapSize + PANEL_POSITIONING.FULLSCREEN_RIGHT_OFFSET,
        top:
          headerHeight +
          gapSize +
          PANEL_POSITIONING.FULLSCREEN_TOP_BASE +
          PANEL_POSITIONING.FULLSCREEN_TOP_EXTRA +
          PANEL_POSITIONING.FULLSCREEN_TOP_ADDITIONAL,
      }
    : {
        right: PANEL_POSITIONING.RIGHT_OFFSET,
        top: PANEL_POSITIONING.TOP_OFFSET,
      };

  return (
    <PanelWrapper isFullScreen={isFullScreen}>
      <FloatingPanels
        key={`org-chart-panel-${isFullScreen ? "fullscreen" : "normal"}-${
          forcePanelId || defaultPanelId
        }`}
        panels={panelsToDisplay}
        defaultSelectedPanelId={forcePanelId || defaultPanelId}
        containerHeight={containerHeight}
        position={position}
        alignPanels="right"
        eventBus={EventBus}
        zIndex={
          isFullScreen ? Z_INDEX.PANELS : undefined
        } /* Above canvas (z-index: 10001) */
      />
    </PanelWrapper>
  );
};

OrgChartPanel.propTypes = {
  hideDetailsPanelInitially: PropTypes.bool,
  isFullScreen: PropTypes.bool,
  headerHeight: PropTypes.number,
};

export default OrgChartPanel;
